'use client';

import { ShoppingBag } from 'lucide-react';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Trans } from '@kit/ui/trans';

interface LocalizedText {
  en?: string;
  [locale: string]: string | undefined;
}

function getLocalizedText(value: unknown, locale = 'en'): string {
  if (!value || typeof value !== 'object') return '';
  const map = value as LocalizedText;
  return map[locale] ?? map['en'] ?? '';
}

export interface ArticleLike {
  id: string;
  title: unknown;
  description: unknown;
  features: unknown;
  price_eur: number | null;
  price_usd: number | null;
  is_subscription: boolean;
  subscription_interval: string | null;
  featured_image_id: string | null;
  external_url: string | null;
  allow_backorder?: boolean;
  track_inventory: boolean;
  inventory_count: number | null;
}

interface ArticleDetailDialogProps {
  article: ArticleLike | null;
  currency: string;
  isOpen: boolean;
  isPurchasePending: boolean;
  onClose: () => void;
  onBuy: (article: ArticleLike) => void;
  formatCurrency: (amount: number | null, currency: string) => string;
}

function isOutOfStock(article: ArticleLike): boolean {
  if (!article.track_inventory) return false;
  if (article.allow_backorder) return false;
  return (article.inventory_count ?? 0) <= 0;
}

export function ArticleDetailDialog({
  article,
  currency,
  isOpen,
  isPurchasePending,
  onClose,
  onBuy,
  formatCurrency,
}: ArticleDetailDialogProps) {
  if (!article) return null;

  const title = getLocalizedText(article.title);
  const description = getLocalizedText(article.description);
  const price = currency === 'USD' ? article.price_usd : article.price_eur;
  const outOfStock = isOutOfStock(article);

  // Features may be array of localized objects or plain strings
  const featuresRaw = Array.isArray(article.features) ? article.features : [];
  const features = featuresRaw.map((f) => {
    if (typeof f === 'string') return f;
    return getLocalizedText(f);
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-8 text-xl">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product image */}
          {article.featured_image_id ? (
            <div className="bg-muted relative aspect-video overflow-hidden rounded-lg">
              {/* eslint-disable-next-line @next/next/no-img-element -- /api/media endpoint 302s to Supabase signed / R2 media; image-rendering.md forbids next/image on media */}
              <img
                src={`/api/media/${article.featured_image_id}`}
                alt={title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="bg-muted flex aspect-video items-center justify-center rounded-lg">
              <ShoppingBag className="text-muted-foreground h-16 w-16" />
            </div>
          )}

          {/* Price + subscription badge */}
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold">
              {price !== null ? (
                article.is_subscription ? (
                  `${formatCurrency(price, currency)} / ${article.subscription_interval ?? 'month'}`
                ) : (
                  formatCurrency(price, currency)
                )
              ) : (
                <Trans i18nKey="shop:priceTBD" defaults="Price on request" />
              )}
            </span>
            {article.is_subscription && (
              <Badge variant="secondary">
                <Trans i18nKey="shop:subscription" defaults="Subscription" />
              </Badge>
            )}
            {outOfStock && (
              <Badge variant="destructive">
                <Trans i18nKey="shop:outOfStock" defaults="Out of stock" />
              </Badge>
            )}
          </div>

          {/* Description */}
          {description && (
            <p className="text-muted-foreground leading-relaxed">
              {description}
            </p>
          )}

          {/* Features list */}
          {features.length > 0 && (
            <div>
              <h3 className="mb-2 font-semibold">
                <Trans i18nKey="shop:features" defaults="Features" />
              </h3>
              <ul className="text-muted-foreground space-y-1 text-sm">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Buy action */}
          <div className="flex gap-3 pt-2">
            <Button
              className="flex-1"
              disabled={outOfStock || price === null || isPurchasePending}
              onClick={() => onBuy(article)}
            >
              {isPurchasePending ? (
                <Trans i18nKey="shop:processing" defaults="Processing..." />
              ) : article.external_url ? (
                <Trans i18nKey="shop:viewProduct" defaults="View Product" />
              ) : (
                <Trans i18nKey="shop:buyNow" defaults="Buy now" />
              )}
            </Button>
            <Button variant="outline" onClick={onClose}>
              <Trans i18nKey="common:cancel" defaults="Cancel" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
